<template>
  <div
    class="employee-column relative"
    style="min-width: 240px; flex: 1 1 240px"
    @dragover.prevent="onDragOver"
    @drop="onDrop"
    @dragleave="$emit('drag-leave')">
    <!-- Sticky Header -->
    <div
      class="column-header wc-col-header"
      :class="{ 'cursor-pointer hover-opacity': $vuetify.breakpoint.smAndDown }"
      @click="$emit('click-header', col)">
      <div class="d-flex align-center" style="gap: 10px; overflow: hidden; flex: 1">
        <v-badge bottom overlap :color="col.liveStatus.color" dot offset-x="10" offset-y="10">
          <v-avatar size="40" class="wc-col-avatar">
            <img :src="col.picture ? '/img/profilbilder/' + col.picture : '/img/default-avatar.png'" style="object-fit: cover" />
          </v-avatar>
        </v-badge>
        <div style="overflow: hidden">
          <div class="wc-col-name">{{ col.firstName }}</div>
          <div class="wc-col-time">
            <v-icon x-small style="color: rgba(255, 255, 255, 0.3); font-size: 11px" class="mr-1">mdi-clock-outline</v-icon>
            {{ col.shiftRange }}
          </div>
        </div>
      </div>

      <!-- Column Quick Stats -->
      <div class="d-flex align-center" style="gap: 6px">
        <div v-if="appointmentCount > 0" class="wc-col-badge">
          <v-icon x-small style="font-size: 10px">mdi-calendar</v-icon>
          {{ appointmentCount }}
        </div>
        <div v-if="taskCount > 0" class="wc-col-badge wc-col-badge-task">
          <v-icon x-small style="font-size: 10px">mdi-checkbox-marked</v-icon>
          {{ taskCount }}
        </div>
        <div
          v-if="notesCount > 0"
          class="wc-col-badge wc-col-badge-note"
          @click.stop="$emit('open-note', col)"
          style="cursor: pointer"
          title="Notizen ansehen">
          <v-icon x-small style="font-size: 10px">mdi-message-text</v-icon>
          {{ notesCount }}
        </div>
        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" @click.stop :style="isDarkMode ? 'color: rgba(255, 255, 255, 0.3)' : 'color: black'">
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense dark class="rounded-lg" style="background: #1a1a1a">
            <v-list-item @click="$emit('open-note', col)">
              <v-icon small left color="blue lighten-2">mdi-message-text</v-icon>
              <v-list-item-title>Notiz senden</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Lane Body -->
    <div class="lane-body relative" :style="{ height: hoursCount * hourHeight + 80 + 'px' }" ref="laneBody">
      <!-- Grid Lines -->
      <div v-for="h in hoursCount" :key="'g' + h" class="grid-line" :style="{ top: (h - 1) * hourHeight + 'px' }"></div>
      <!-- Shift Backgrounds -->
      <div
        v-for="(bg, idx) in col.backgrounds"
        :key="'bg' + idx"
        class="shift-background"
        :style="{ top: bg.top + 'px', height: bg.height + 'px', backgroundColor: bg.bgColor, borderLeft: `3px solid ${bg.color}` }"></div>

      <!-- Appointment/Task Blocks -->
      <div
        v-for="(block, bIdx) in col.items"
        :key="'blk' + bIdx"
        class="wc-block"
        :class="[
          block.class,
          {
            'status-cancelled': block.status === 'cancelled',
            'status-checkedin': block.status === 'checkedin',
            'status-noshow': block.status === 'noshow',
            'wc-block-small': block.height < 55,
            'wc-block-medium': block.height >= 55 && block.height < 90,
          },
        ]"
        :style="{
          top: block.top + 'px',
          height: block.height + 'px',
          borderLeft: `4px solid ${block.borderColor}`,
          ...block.style,
        }"
        @click="$emit('click-item', block, col)">
        <!-- 🔴 Editing Presence Indicator -->
        <transition name="editing-fade">
          <div v-if="editingUsers && editingUsers[block._id]" class="wc-editing-indicator">
            <div class="wc-editing-pulse"></div>
            <div class="wc-editing-badge">
              <v-icon x-small color="white" style="font-size: 10px">mdi-pencil</v-icon>
              <span>{{ editingUsers[block._id].userName }}</span>
            </div>
          </div>
        </transition>
        <!-- Small Block (< 55px) -->
        <div v-if="block.height < 55" class="wc-block-inner d-flex align-center px-2" style="height: 100%">
          <v-icon x-small :color="block.iconColor" class="mr-1" style="font-size: 12px">{{ block.icon }}</v-icon>
          <span class="wc-block-time mr-2">{{ block.start }}</span>
          <span class="wc-block-title flex-grow-1 text-truncate">{{ block.title }}</span>
        </div>

        <!-- Normal Block -->
        <div v-else class="wc-block-inner d-flex flex-column pa-2" style="height: 100%">
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="d-flex align-center">
              <v-icon x-small :color="block.iconColor" class="mr-1" style="font-size: 13px">{{ block.icon }}</v-icon>
              <span class="wc-block-time">{{ block.start }} - {{ block.end }}</span>
            </div>
            <!-- Status indicator -->
            <v-icon v-if="block.status === 'checkedin'" x-small color="green accent-3" style="font-size: 12px">mdi-check-circle</v-icon>
            <v-icon v-else-if="block.status === 'noshow'" x-small color="red accent-2" style="font-size: 12px">mdi-alert-circle</v-icon>
          </div>
          <div class="wc-block-title text-truncate">{{ block.title }}</div>
          <div v-if="block.subtitle && block.height >= 80" class="wc-block-subtitle text-truncate mt-auto">
            {{ block.subtitle }}
          </div>
        </div>

        <!-- Hover Quick Actions -->
        <div class="wc-block-actions" v-if="block.type !== 'system'">
          <v-btn x-small icon dark @click.stop="$emit('click-item', block, col)" class="wc-action-btn">
            <v-icon x-small>mdi-pencil</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Ghost Block (Drag Preview) -->
      <div
        v-if="isHovered"
        class="ghost-block d-flex align-center justify-center flex-column"
        :class="{ 'error-ghost': hoverState.error }"
        :style="{ top: hoverState.top + 'px', height: hoverState.height + 'px' }">
        <span class="white--text font-weight-bold" style="font-size: 1.1rem; text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5)">{{ hoverState.time }}</span>
        <span v-if="hoverState.error" class="wc-ghost-error mt-1">{{ hoverState.error }}</span>
      </div>

      <!-- Click Grid -->
      <div class="click-grid-layer" @click.self="$emit('click-empty', $event, col)"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WorkCalendarColumn",
  props: {
    col: Object,
    isDarkMode: Boolean,
    hoursCount: Number,
    hourHeight: Number,
    headerOffset: Number,
    hoverState: Object,
    editingUsers: { type: Object, default: () => ({}) },
  },
  computed: {
    isHovered() {
      return this.hoverState && this.hoverState.colId === this.col.id
    },
    appointmentCount() {
      return (this.col.items || []).filter((b) => b.isAppt).length
    },
    taskCount() {
      return (this.col.items || []).filter((b) => b.isTask).length
    },
    notesCount() {
      return (this.col.notes || []).length
    },
  },
  methods: {
    onDragOver(e) {
      const rect = this.$refs.laneBody.getBoundingClientRect()
      const relativeY = e.clientY - rect.top
      this.$emit("drag-over", { event: e, colId: this.col.id, relativeY })
    },
    onDrop(e) {
      this.$emit("drop", { event: e, col: this.col })
    },
  },
}
</script>

<style scoped>
.employee-column {
  border-right: 1px solid var(--border-light);
}

.wc-col-header {
  position: -webkit-sticky; /* WICHTIG FÜR IPAD */
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-medium);

  /* ===== NEU: DIE LÖSUNG ===== */
  z-index: 10; /* Über Blöcken (5) und Zeitlinie (3), aber unter Overlays/Sidebar */
  height: 80px; /* Hält den Header millimetergenau auf der Höhe des linken Offsets */
}
.wc-col-avatar {
  border: 2px solid var(--border-strong) !important;
  flex-shrink: 0;
}
.wc-col-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}
.wc-col-time {
  font-size: 0.75rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Column Badges */
.wc-col-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-muted);
  background: var(--bg-panel);
  padding: 2px 6px;
  border-radius: 4px;
}
.wc-col-badge .v-icon {
  color: var(--icon-muted) !important;
}
.wc-col-badge-task {
  color: rgba(171, 71, 188, 0.9);
}
.wc-col-badge-note {
  color: rgba(66, 165, 245, 0.9);
  background: rgba(66, 165, 245, 0.1);
}
.wc-col-badge-note:hover {
  background: rgba(66, 165, 245, 0.2);
}
.wc-col-badge-note .v-icon {
  color: rgba(66, 165, 245, 0.8) !important;
}

.relative {
  position: relative;
}

/* ===== GRID ===== */
.grid-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--bg-time-line);
  pointer-events: none;
}

/* ===== SHIFT BACKGROUND ===== */
.shift-background {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;
  pointer-events: none;
}

/* ===== BLOCKS ===== */
.wc-block {
  position: absolute;
  left: 6px;
  right: 6px;
  z-index: 5;
  cursor: pointer;
  background: var(--bg-block);
  border-radius: 10px;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent; /* Kein blaues Highlight auf Mobile */
}
/* Hover-Effekte NUR auf Geräten mit Maus/Trackpad */
@media (hover: hover) {
  .wc-block:hover {
    transform: translateY(-1px) scale(1.01);
    z-index: 8;
    box-shadow: 0 8px 24px var(--shadow-color);
  }
}
/* Touch-Feedback auf Mobile: subtiler Active-State */
@media (hover: none) {
  .wc-block:active {
    transform: scale(0.98);
    opacity: 0.85;
  }
}
.wc-block-inner {
  position: relative;
  z-index: 1;
}

/* Block Text */
.wc-block-time {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-muted);
  white-space: nowrap;
}
.wc-block-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-main);
  line-height: 1.3;
}
.wc-block-subtitle {
  font-size: 0.7rem;
  color: var(--text-muted);
}
.wc-block-small .wc-block-title {
  font-size: 0.75rem;
}
.wc-block-small .wc-block-time {
  font-size: 0.65rem;
}

/* Hover Actions — nur auf Desktop sichtbar */
.wc-block-actions {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 2;
}
@media (hover: hover) {
  .wc-block:hover .wc-block-actions {
    opacity: 1;
  }
}
.wc-action-btn {
  width: 24px !important;
  height: 24px !important;
  background: var(--bg-panel-dark) !important;
  color: var(--text-main) !important;
}

/* ===== SPECIAL BLOCK TYPES ===== */
.item-pause {
  background: repeating-linear-gradient(
    45deg,
    var(--bg-panel),
    var(--bg-panel) 10px,
    var(--bg-panel-hover) 10px,
    var(--bg-panel-hover) 20px
  ) !important;
  border-left-color: #ff9800 !important;
}
.item-system {
  pointer-events: none;
  border-left: 4px solid #d32f2f !important;
  background: repeating-linear-gradient(
    45deg,
    rgba(211, 47, 47, 0.1),
    rgba(211, 47, 47, 0.1) 10px,
    rgba(211, 47, 47, 0.05) 10px,
    rgba(211, 47, 47, 0.05) 20px
  ) !important;
}
.glass-red {
  border: 1px solid rgba(244, 67, 54, 0.3);
}

/* ===== STATUSES ===== */
.status-cancelled {
  opacity: 0.4;
  filter: grayscale(100%);
}
.status-checkedin {
  box-shadow: inset 0 0 0 1px rgba(0, 230, 118, 0.2);
}
.status-noshow {
  box-shadow: inset 0 0 0 1px rgba(255, 82, 82, 0.2);
}

/* ===== GHOST ===== */
.ghost-block {
  position: absolute;
  left: 6px;
  right: 6px;
  z-index: 15;
  background: rgba(76, 175, 80, 0.15);
  border: 2px dashed rgba(76, 175, 80, 0.5);
  pointer-events: none;
  border-radius: 10px;
  will-change: top, height;
}
.error-ghost {
  background: rgba(244, 67, 54, 0.12);
  border-color: rgba(244, 67, 54, 0.4);
}
.wc-ghost-error {
  font-size: 0.65rem;
  font-weight: 600;
  color: #ef5350;
  background: rgba(0, 0, 0, 0.6);
  padding: 2px 8px;
  border-radius: 4px;
}

/* ===== CLICK GRID ===== */
.click-grid-layer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  cursor: crosshair;
}
.cursor-pointer {
  cursor: pointer;
}
.hover-opacity {
  transition: opacity 0.15s ease;
}
@media (hover: hover) {
  .hover-opacity:hover {
    opacity: 0.75;
  }
}
/* ===== EDITING PRESENCE INDICATOR ===== */
.wc-editing-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  pointer-events: none;
  border-radius: 10px;
  overflow: hidden;
}
.wc-editing-pulse {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 10px;
  border: 2px solid rgba(255, 193, 7, 0.6);
  animation: editPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  box-shadow: inset 0 0 20px rgba(255, 193, 7, 0.08);
}
@keyframes editPulse {
  0%,
  100% {
    border-color: rgba(255, 193, 7, 0.6);
    box-shadow: 0 0 8px rgba(255, 193, 7, 0.15), inset 0 0 20px rgba(255, 193, 7, 0.08);
  }
  50% {
    border-color: rgba(255, 193, 7, 0.25);
    box-shadow: 0 0 2px rgba(255, 193, 7, 0.05), inset 0 0 10px rgba(255, 193, 7, 0.03);
  }
}
.wc-editing-badge {
  position: absolute;
  top: -1px;
  right: -1px;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px 2px 5px;
  background: linear-gradient(135deg, #ffa000, #ff8f00);
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  border-radius: 0 10px 0 8px;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.4);
  animation: editBadgeIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes editBadgeIn {
  0% {
    opacity: 0;
    transform: scale(0.5) translateX(10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateX(0);
  }
}
.editing-fade-enter-active {
  transition: opacity 0.3s ease;
}
.editing-fade-leave-active {
  transition: opacity 0.2s ease;
}
.editing-fade-enter,
.editing-fade-leave-to {
  opacity: 0;
}
</style>
